import { useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { bulkInvitations } from "@/lib/inviteApi";
import { errMsg } from "@/lib/applyApi";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const FILTERS = [["active", "Active"], ["archived", "Archived"], ["all", "All"]];
export const inView = (inv, view) => view === "all" || (view === "archived" ? !!inv.archived_at : !inv.archived_at);

// Runs archive / restore / delete through one endpoint and reports anything the server refused.
export async function runAction(ids, action, testOnly, onResult) {
  try {
    const r = await bulkInvitations(ids, action, testOnly);
    const verb = { archive: "archived", restore: "restored", delete: "deleted" }[action];
    if (r.done.length) toast.success(`${r.done.length} invitation${r.done.length === 1 ? "" : "s"} ${verb}`);
    if (r.skipped.length) toast.warning(`${r.skipped.length} skipped: ${[...new Set(r.skipped.map((s) => s.reason))].join(" ")}`);
    onResult(r, action);
  } catch (e) { toast.error(errMsg(e, "Action failed")); }
}

export function ConfirmDelete({ count, open, onCancel, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="invite-delete-confirm" className="w-[calc(100%-1.5rem)] max-w-md rounded-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete {count === 1 ? "this invitation" : `${count} invitations`}?</AlertDialogTitle>
          <AlertDialogDescription>The link stops working immediately. Applications, agreements and seller accounts are never deleted; invitations that aren't safe to delete are skipped — archive those instead.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="invite-delete-cancel" className="h-11 rounded-full">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="invite-delete-confirm-btn" onClick={onConfirm} className="h-11 rounded-full bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RowMenu({ inv, i, onResult }) {
  const [confirm, setConfirm] = useState(false);
  const run = (a) => runAction([inv.id], a, false, onResult);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={`invite-menu-${i}`} aria-label="More actions" className="h-10 w-10 sm:h-9 sm:w-9 inline-flex items-center justify-center rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50">
          <MoreHorizontal className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {inv.archived_at
            ? <DropdownMenuItem data-testid={`invite-restore-${i}`} onSelect={() => run("restore")} className="min-h-[44px]"><ArchiveRestore className="h-4 w-4 mr-2" />Restore</DropdownMenuItem>
            : <DropdownMenuItem data-testid={`invite-archive-${i}`} onSelect={() => run("archive")} className="min-h-[44px]"><Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>}
          <DropdownMenuItem data-testid={`invite-delete-${i}`} disabled={!!inv.delete_block} onSelect={() => setConfirm(true)} className="min-h-[44px] text-red-700 items-start">
            <Trash2 className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
            <span>Delete{inv.delete_block && <span className="block text-[11px] text-stone-500">{inv.delete_block}</span>}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDelete count={1} open={confirm} onCancel={() => setConfirm(false)} onConfirm={() => { setConfirm(false); run("delete"); }} />
    </>
  );
}

export function InviteBulkBar({ ids, setIds, visible, testOnly, onResult, onCancel }) {
  const [confirm, setConfirm] = useState(false);
  const n = ids.size;
  const run = (a) => runAction([...ids], a, testOnly, (r, act) => { setIds(new Set()); onResult(r, act); });
  const btn = "h-10 px-3 rounded-full text-xs font-semibold disabled:opacity-40";
  return (
    <div data-testid="invite-bulk-bar" className="mt-2 rounded-xl bg-stone-900 text-white p-2.5 flex flex-wrap items-center gap-2">
      <span data-testid="invite-bulk-count" className="text-sm font-semibold mr-1">{n} selected</span>
      <button type="button" data-testid="invite-bulk-select-all" onClick={() => setIds(new Set(visible.map((r) => r.id)))} className={`${btn} bg-white/10`}>Select all shown ({visible.length})</button>
      <span className="ml-auto" />
      <button type="button" data-testid="invite-bulk-archive" disabled={!n} onClick={() => run("archive")} className={`${btn} bg-amber-700 hover:bg-amber-600`}>Archive</button>
      <button type="button" data-testid="invite-bulk-restore" disabled={!n} onClick={() => run("restore")} className={`${btn} bg-white/15 hover:bg-white/25`}>Restore</button>
      <button type="button" data-testid="invite-bulk-delete" disabled={!n} onClick={() => setConfirm(true)} className={`${btn} bg-red-700 hover:bg-red-600`}>Delete</button>
      <button type="button" data-testid="invite-bulk-cancel" onClick={onCancel} className={`${btn} bg-white/10`}>Cancel</button>
      <ConfirmDelete count={n} open={confirm} onCancel={() => setConfirm(false)} onConfirm={() => { setConfirm(false); run("delete"); }} />
    </div>
  );
}
